/**
 * SEE-214/SEE-217: WebAuthn ceremonies inside the native mobile shell.
 *
 * Apple does not expose WebAuthn to embedded WKWebViews (only Safari and
 * browser-entitled apps get it), so when this web app runs inside the
 * Speedchain shell the create()/get() ceremonies are delegated to the native
 * layer over the established WebView message bridge:
 *
 *   web    --postMessage-->  { type: 'PASSKEY_CEREMONY', requestId, mode, requestJson }
 *   native runs the OS passkey sheet (react-native-passkey)
 *   native --CustomEvent 'PASSKEY_RESULT_FROM_NATIVE'--> { requestId, ok, responseJson | errorCode }
 *
 * The shell announces the capability by defining
 * window.__SPEEDCHAIN_NATIVE_PASSKEYS__ before the page loads, and only when
 * the device OS supports passkeys. Version skew is safe by construction:
 * an older app never defines the flag, so the web app keeps treating the
 * shell as passkey-less (password login, no prompt); a newer app against an
 * older web build just has an idle message handler.
 *
 * Error mapping preserves the error contracts the web flows already branch
 * on (see passkeyClient/useCreatePasskey): a cancelled native sheet surfaces
 * as NotAllowedError (isUserCancellation), an already-enrolled authenticator
 * as InvalidStateError (the "alreadyRegistered" branch).
 */

interface NativeBridgeWindow extends Window {
  ReactNativeWebView?: { postMessage: (message: string) => void };
  __SPEEDCHAIN_NATIVE_PASSKEYS__?: boolean;
}

/** Same UA contract the shell sets and authSlice matches: SpeedchainMobile/<os>/<version>. */
const MOBILE_UA_REGEX = /SpeedchainMobile\/[^/]+\/\d+(\.\d+)*/;

/** Event the native layer dispatches into the page with a ceremony result. */
export const PASSKEY_RESULT_EVENT = 'PASSKEY_RESULT_FROM_NATIVE';

/** Message type the native layer listens for. */
export const PASSKEY_CEREMONY_MESSAGE = 'PASSKEY_CEREMONY';

/**
 * Ceremony budget. The OS sheet itself has no fixed deadline (Face ID retries,
 * iCloud passkey sync prompts), so this is a safety net against a native layer
 * that never answers — not a UX timer.
 */
export const CEREMONY_TIMEOUT_MS = 120_000;

/**
 * react-native-passkey error codes → the DOM exception names the web flows
 * already understand. Unmapped codes keep their native name and fall into the
 * generic-failure branches (Sentry + retryable error text).
 */
const ERROR_NAME_BY_NATIVE_CODE: Record<string, string> = {
  // User dismissed the sheet / let it expire — the silent, non-error paths.
  UserCancelled: 'NotAllowedError',
  TimedOut: 'NotAllowedError',
  // get() with no usable credential — treated like a dismissal so login falls
  // back to the password step without Sentry noise.
  NoCredentials: 'NotAllowedError',
  Interrupted: 'AbortError',
  // excludeCredentials matched: this authenticator already holds a passkey.
  CredentialAlreadyExists: 'InvalidStateError',
};

export function isMobileAppShell(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    MOBILE_UA_REGEX.test(navigator.userAgent)
  );
}

/** True when the shell has announced native passkey support for this device. */
export function isNativePasskeyAvailable(): boolean {
  if (typeof window === 'undefined') return false;
  const w = window as NativeBridgeWindow;
  return Boolean(w.__SPEEDCHAIN_NATIVE_PASSKEYS__) && Boolean(w.ReactNativeWebView);
}

function nativeError(code: string | undefined, message: string | undefined): Error {
  const resolvedCode = code || 'UnknownError';
  const error = new Error(message || `Native passkey ceremony failed (${resolvedCode})`);
  error.name = ERROR_NAME_BY_NATIVE_CODE[resolvedCode] ?? resolvedCode;
  return error;
}

/**
 * WebAuthn JSON carries binary fields base64url-encoded WITHOUT padding.
 * Browsers guarantee that; the native path crosses more layers, so normalize
 * defensively (a plain-base64 value becomes base64url, an already-correct
 * value is untouched).
 */
function toBase64Url(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  return value.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

interface CeremonyResponseJSON {
  id: string;
  rawId: string;
  type?: string;
  authenticatorAttachment?: string | null;
  clientExtensionResults?: Record<string, unknown>;
  response: Record<string, unknown>;
}

function normalizeCeremonyResponse(parsed: CeremonyResponseJSON): CeremonyResponseJSON {
  const response = { ...(parsed.response || {}) };
  for (const field of [
    'clientDataJSON',
    'attestationObject',
    'authenticatorData',
    'signature',
    'userHandle',
  ]) {
    if (field in response) {
      response[field] = toBase64Url(response[field]);
    }
  }
  return {
    ...parsed,
    id: toBase64Url(parsed.id) as string,
    rawId: toBase64Url(parsed.rawId ?? parsed.id) as string,
    // @simplewebauthn's server-side verification requires both of these; the
    // native module may omit them.
    type: parsed.type ?? 'public-key',
    clientExtensionResults: parsed.clientExtensionResults ?? {},
    response,
  };
}

interface PasskeyResultDetail {
  requestId: string;
  ok: boolean;
  responseJson?: string;
  errorCode?: string;
  errorMessage?: string;
}

function newRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `pk-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Run a WebAuthn ceremony in the native layer. `optionsJSON` is the parsed
 * PublicKeyCredentialCreationOptionsJSON / PublicKeyCredentialRequestOptionsJSON
 * the backend produced; resolves with the standard registration/authentication
 * response JSON (same shape @simplewebauthn/browser returns — pass the
 * matching Registration/AuthenticationResponseJSON as T), rejects with an
 * Error whose `name` follows the browser WebAuthn conventions.
 */
export function runNativePasskeyCeremony<T = CeremonyResponseJSON>(
  mode: 'create' | 'get',
  optionsJSON: object
): Promise<T> {
  const bridge = (window as NativeBridgeWindow).ReactNativeWebView;
  if (!bridge) {
    return Promise.reject(new Error('Native passkey bridge is not available'));
  }

  const requestId = newRequestId();

  return new Promise<T>((resolve, reject) => {
    // `timeout` is initialized below, before anything can invoke cleanup.
    const cleanup = () => {
      window.removeEventListener(PASSKEY_RESULT_EVENT, onResult);
      clearTimeout(timeout);
    };

    const onResult = (event: Event) => {
      const detail = (event as CustomEvent<PasskeyResultDetail>).detail;
      if (!detail || detail.requestId !== requestId) return;
      cleanup();
      if (detail.ok && detail.responseJson) {
        try {
          // Structurally the caller's Registration/AuthenticationResponseJSON;
          // the backend verifies it cryptographically either way.
          resolve(normalizeCeremonyResponse(JSON.parse(detail.responseJson)) as unknown as T);
        } catch (parseError) {
          reject(parseError instanceof Error ? parseError : new Error('Unparseable native passkey response'));
        }
      } else {
        reject(nativeError(detail.errorCode, detail.errorMessage));
      }
    };

    const timeout = setTimeout(() => {
      cleanup();
      // A vanished native layer is a bridge failure, not a user choice: use a
      // non-cancellation name so create() shows its retryable error (and login
      // still falls back to the password step).
      const error = new Error('Native passkey ceremony timed out');
      error.name = 'NativePasskeyTimeout';
      reject(error);
    }, CEREMONY_TIMEOUT_MS);

    window.addEventListener(PASSKEY_RESULT_EVENT, onResult);
    bridge.postMessage(
      JSON.stringify({
        type: PASSKEY_CEREMONY_MESSAGE,
        requestId,
        mode,
        requestJson: JSON.stringify(optionsJSON),
      })
    );
  });
}
